import React, { useState, useEffect } from 'react'
import { AiOutlineCloseCircle } from 'react-icons/ai'
import { FiCalendar } from 'react-icons/fi'
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'
import { IoChevronDown, IoChevronUp } from 'react-icons/io5'
import { ErrorNotification, SuccessNotification } from '../../utils'
import { getOrderItemImage } from '../../utils/productImage'
import {
  canAdvanceOrderStatus,
  canBookOrderAppointment,
  getNextPatchStatus,
  getOrderPrimaryItemType,
  getOrderStatusLabel,
  getStatusActionLabel,
  getStatusFlow,
  getStatusStepState,
  isServiceOrder,
  resolveFlowStatus,
  ORDER_STATUS,
  patchOrderStatus,
  formatOrderStatusError,
} from '../../utils/orderStatus'

const ServiceOrderDetail = ({
  isOpen,
  setIsOpen,
  order,
  onDelete,
  onOrderUpdated,
  onScheduleAppointment,
}) => {
  const [showCustomer, setShowCustomer] = useState(false)
  const [drawerWidth, setDrawerWidth] = useState(600)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 700) {
        setDrawerWidth(window.innerWidth - 20)
      } else {
        setDrawerWidth(600)
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const advanceStatus = async () => {
    if (!order?.id) return
    const nextStatus = getNextPatchStatus(order)
    if (!nextStatus) return

    setLoading(true)
    try {
      const updated = await patchOrderStatus(order.id, nextStatus)
      SuccessNotification(`Order moved to ${getOrderStatusLabel(nextStatus)}`)
      onOrderUpdated?.(updated)
    } catch (error) {
      ErrorNotification(formatOrderStatusError(error))
    } finally {
      setLoading(false)
    }
  }

  const orderItems = Array.isArray(order?.items) ? order.items : []
  const orderNo = order?.order_number || order?.id || '—'
  const status = resolveFlowStatus(order)
  const statusLabel = getOrderStatusLabel(order?.status)
  const amount = order?.total_amount ? parseFloat(order.total_amount) : 0
  const dateOrdered = order?.created_at
    ? new Date(order.created_at).toLocaleDateString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—'
  const orderType = getOrderPrimaryItemType(order)
  const statusFlow = getStatusFlow(order)
  const nextStatus = getNextPatchStatus(order)
  const showAdvance = canAdvanceOrderStatus(order)
  const showAppointment = canBookOrderAppointment(order)
  const totalItemCount =
    order?.total_items ??
    orderItems.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0)

  const address = {
    code: order?.gps_address || 'N/A',
    street: order?.delivery_address || 'N/A',
    city:
      (order?.delivery_region ? `${order.delivery_region}, ` : '') +
      (order?.delivery_country || 'Ghana'),
    phone: order?.customer_phone || 'N/A',
  }

  const paymentMethod = order?.payment_method || 'Mobile Money Payment'
  const customer = {
    name: order?.customer_fullname || 'N/A',
    email: order?.customer_email || 'N/A',
    phone: order?.customer_phone || 'N/A',
    gender: order?.customer_gender || 'N/A',
  }

  const stepDotClass = (stepStatus) => {
    const state = getStatusStepState(order, stepStatus)
    if (state === 'complete') return 'bg-green-500'
    if (state === 'current') return 'bg-yellowcolor'
    return 'bg-gray-300 dark:bg-gray-600'
  }

  return (
    <Drawer
      open={isOpen}
      onClose={() => setIsOpen(false)}
      direction="right"
      size={drawerWidth}
    >
      <div className="w-full h-[100vh] overflow-y-auto bg-[#fff] dark:bg-dark">
        <div className="w-full h-full">
          <div className="p-4 w-full flex flex-row items-center justify-between border-b border-gray-100 dark:border-border-dark relative">
            <h1 className="text-xl font-bold dark:text-white">Order details</h1>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <AiOutlineCloseCircle className="h-7 w-7" />
            </button>
          </div>

          <div className="px-6 py-4 border-b border-gray-100 dark:border-border-dark space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-row items-center gap-4">
                <span className="text-yellowcolor font-semibold">Order Status</span>
                <span className="bg-gray-100 text-gray-700 dark:bg-secondaryDark dark:text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-2 capitalize">
                  <span className="w-2 h-2 rounded-full bg-yellowcolor inline-block" />
                  {statusLabel}
                </span>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-secondaryDark text-gray-600 dark:text-gray-300 capitalize">
                  {orderType}
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                {showAdvance && (
                  <button
                    type="button"
                    onClick={advanceStatus}
                    disabled={loading}
                    className="bg-yellowcolor hover:bg-yellowcolor/80 text-sm text-black px-4 py-2 rounded-md font-medium disabled:opacity-60"
                  >
                    {loading ? 'Updating...' : getStatusActionLabel(nextStatus)}
                  </button>
                )}
                {showAppointment && (
                  <button
                    type="button"
                    onClick={() => onScheduleAppointment?.(order)}
                    className="border border-yellowcolor text-yellowcolor hover:bg-yellowcolor/10 text-sm px-4 py-2 rounded-md font-medium flex items-center gap-2"
                  >
                    <FiCalendar size={16} />
                    Schedule Appointment
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {isServiceOrder(order) ? 'Service flow' : 'Product flow'}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {statusFlow.map((step, index) => (
                  <React.Fragment key={step}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${stepDotClass(step)}`} />
                      <span
                        className={`text-xs capitalize ${
                          getStatusStepState(order, step) === 'current'
                            ? 'font-semibold text-gray-900 dark:text-white'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {getOrderStatusLabel(step)}
                      </span>
                    </div>
                    {index < statusFlow.length - 1 && (
                      <span className="text-gray-300 dark:text-gray-600 text-xs">→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              {isServiceOrder(order) && status === ORDER_STATUS.CONFIRMATION && !showAppointment && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Appointment has been scheduled for this order.
                </p>
              )}
              {isServiceOrder(order) && status === ORDER_STATUS.PENDING && !showAppointment && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Confirm the order first, then schedule an appointment.
                </p>
              )}
              {isServiceOrder(order) && status === ORDER_STATUS.PENDING && showAppointment && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  You can schedule an appointment now, or confirm the order first.
                </p>
              )}
            </div>
          </div>

          <div className="m-6 space-y-3">
            <div className="flex items-center justify-between px-1">
              <div className="text-gray-500 text-xs">
                Order No.: <span className="font-bold text-black dark:text-white">{orderNo}</span>
              </div>
              <div className="text-gray-500 text-xs">
                {orderItems.length} line{orderItems.length === 1 ? '' : 's'}
                {totalItemCount ? ` · ${totalItemCount} item${totalItemCount === 1 ? '' : 's'}` : ''}
              </div>
            </div>

            {orderItems.length === 0 ? (
              <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-xl p-4 text-sm text-gray-500">
                No items on this order.
              </div>
            ) : (
              orderItems.map((item) => {
                const itemName = item?.product_name || item?.product?.name || 'Product'
                const itemQty = item?.quantity || 1
                const itemImg = getOrderItemImage(item)
                const unitPrice = item?.unit_price ? parseFloat(item.unit_price) : 0
                const lineSubtotal = item?.subtotal
                  ? parseFloat(item.subtotal)
                  : unitPrice * itemQty

                return (
                  <div
                    key={item?.id || `${item?.product_id}-${itemName}`}
                    className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-xl p-4 flex flex-row items-center gap-6"
                  >
                    <img
                      src={itemImg}
                      alt={itemName}
                      className="w-20 h-20 object-contain rounded-lg bg-gray-50"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-base">{itemName}</div>
                      <div className="text-gray-500 text-sm mt-1">
                        Qty: <span className="font-bold text-black dark:text-white">{itemQty}</span>
                        {item?.item_type && (
                          <span className="ml-2 capitalize text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-secondaryDark">
                            {item.item_type}
                          </span>
                        )}
                      </div>
                      {(item?.product_description || item?.product?.description) && (
                        <div className="text-gray-500 text-xs mt-1 line-clamp-2">
                          {item.product_description || item.product?.description}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-gray-500 text-xs">Unit Price</div>
                      <div className="font-bold text-lg text-black dark:text-white">
                        ₵
                        {unitPrice.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </div>
                      <div className="text-gray-500 text-xs mt-1">Line total</div>
                      <div className="font-semibold text-sm text-black dark:text-white">
                        ₵
                        {lineSubtotal.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-xl p-6 mx-6 mb-4">
            <div className="font-bold text-base mb-4">Order Information</div>
            <div className="flex flex-col gap-4">
              <div>
                <div className="text-gray-500 text-sm">Date Ordered</div>
                <div className="font-semibold text-sm text-black dark:text-white">{dateOrdered}</div>
              </div>

              <div className="border-t border-gray-100 dark:border-border-dark pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-500 text-sm">Subtotal</span>
                  <span className="font-semibold text-sm text-black dark:text-white">
                    ₵
                    {order?.subtotal
                      ? parseFloat(order.subtotal).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })
                      : '0.00'}
                  </span>
                </div>
                {order?.tax_amount && parseFloat(order.tax_amount) > 0 && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-500 text-sm">Tax</span>
                    <span className="font-semibold text-sm text-black dark:text-white">
                      ₵{parseFloat(order.tax_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {order?.delivery_cost && parseFloat(order.delivery_cost) > 0 && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-500 text-sm">Delivery Cost</span>
                    <span className="font-semibold text-sm text-black dark:text-white">
                      ₵
                      {parseFloat(order.delivery_cost).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center border-t border-gray-100 dark:border-border-dark pt-2">
                  <span className="text-gray-700 font-semibold text-base dark:text-white">
                    Total Amount
                  </span>
                  <span className="font-bold text-lg text-black dark:text-white">
                    ₵{amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-xl p-6 mx-6 mb-4 flex flex-col md:flex-row gap-8">
            <div className="flex-1 space-y-2">
              <div className="text-gray-400 font-semibold mb-2">Delivery Address</div>
              <div className="text-black font-medium text-sm dark:text-white">{address.code}</div>
              <div className="text-black font-medium text-sm dark:text-white">{address.street}</div>
              <div className="text-black font-medium text-sm dark:text-white">{address.city}</div>
              <div className="text-black font-medium text-sm dark:text-white">{address.phone}</div>
              {order?.delivery_notes && (
                <div className="mt-3">
                  <div className="text-gray-500 text-xs">Delivery Notes</div>
                  <div className="text-black font-medium text-sm dark:text-white">
                    {order.delivery_notes}
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <div className="text-black font-semibold mb-2 dark:text-white">Payment Details</div>
              <div className="text-gray-500 text-sm">Method Used</div>
              <div className="font-semibold text-black text-sm dark:text-white">{paymentMethod}</div>
            </div>
          </div>

          {onDelete && (
            <div className="mx-6 mb-6">
              <button
                type="button"
                onClick={() => onDelete(order)}
                className="w-full h-12 rounded-full border border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 font-medium"
              >
                Delete Order
              </button>
            </div>
          )}

          <div className="mx-6 mb-6 bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-xl px-6 py-4">
            <button
              type="button"
              className="w-full flex flex-row items-center justify-between font-semibold text-lg"
              onClick={() => setShowCustomer(!showCustomer)}
            >
              Customer Details
              {showCustomer ? <IoChevronUp size={22} /> : <IoChevronDown size={22} />}
            </button>
            {showCustomer && (
              <>
                <div className="mt-7 bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-xl p-6 flex flex-col gap-6">
                  <div className="flex flex-row items-center gap-6">
                    <div>
                      <div className="text-gray-400 text-sm">Full Name</div>
                      <div className="font-semibold text-black dark:text-white">{customer.name}</div>
                      <div className="text-gray-400 text-sm mt-2">Email</div>
                      <div className="font-semibold text-black dark:text-white">{customer.email}</div>
                    </div>
                  </div>
                </div>
                <div className="mt-7 bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-xl p-6 flex flex-col gap-6">
                  <div>
                    <div className="font-semibold text-black mb-2 dark:text-white">Account Details</div>
                    <div className="flex flex-col gap-1">
                      <div className="text-gray-500 text-sm">Phone Number</div>
                      <div className="font-medium text-black dark:text-white">{customer.phone}</div>
                      <div className="text-gray-500 text-sm mt-2">Gender</div>
                      <div className="font-medium text-black dark:text-white">{customer.gender}</div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  )
}

export default ServiceOrderDetail
